import { useState, useEffect, useCallback } from "react";
import { useArtist } from "../../../context/ArtistContext";
import { useArtistData } from "../../../context/ArtistDataContext";

/**
 * Custom hook to fetch earnings for current artist with auto-refresh
 * Uses ArtistDataContext cache to avoid re-fetching on navigation
 * @param {object} options - Options for filtering (startDate, endDate)
 * @returns {object} - { earnings, totals, loading, error, refetch }
 */
const useEarnings = (options = {}) => {
  const { selectedArtist } = useArtist();
  const { getEarnings, fetchEarnings: fetchEarningsToCache } = useArtistData();
  const [earnings, setEarnings] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { startDate, endDate, autoRefresh = true } = options;

  const artistId = selectedArtist?.artist_id || null;

  // Fetch earnings if not cached
  const fetchEarnings = useCallback(async () => {
    // Check cache first
    const cached = getEarnings(artistId, startDate, endDate);
    if (cached.hasFetched && !cached.loading) {
      setEarnings(cached.earnings || []);
      setTotals(cached.totals || null);
      setError(cached.error);
      setLoading(false);
      return;
    }

    // Fetch and cache
    try {
      setLoading(true);
      setError(null);
      await fetchEarningsToCache(artistId, startDate, endDate);
      
      // Get updated cache
      const updatedCache = getEarnings(artistId, startDate, endDate);
      setEarnings(updatedCache.earnings || []);
      setTotals(updatedCache.totals || null);
      setError(updatedCache.error);
    } catch (err) {
      console.error("Error fetching artist earnings:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, artistId, getEarnings, fetchEarningsToCache]);

  // Use cached data on mount and when dependencies change
  useEffect(() => {
    const cached = getEarnings(artistId, startDate, endDate);
    if (cached.hasFetched && !cached.loading) {
      // Use cached data
      setEarnings(cached.earnings || []);
      setTotals(cached.totals || null);
      setError(cached.error);
      setLoading(false);
    } else if (!cached.loading) {
      // Fetch if not cached and not currently loading
      fetchEarnings();
    } else {
      // Currently loading
      setLoading(true);
    }
  }, [artistId, startDate, endDate, getEarnings, fetchEarnings]);

  // Auto-refresh every 60 seconds (only if autoRefresh is enabled)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchEarnings();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [fetchEarnings, autoRefresh]);

  return {
    earnings,
    totals,
    loading,
    error,
    refetch: fetchEarnings,
  };
};

export default useEarnings;

